import React, { memo, MouseEvent } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Flex } from 'antd';
import type { ItemType } from 'antd/es/menu/interface';
import clsx from 'clsx';
import useIsFeatureAvailable from 'hooks/useIsFeatureAvailable';
import LazySvg from 'components/LazySvg';
import { LINKS } from 'utils/constants';
import { SERVICES } from 'utils/service-entitlements-config';
import { useAppSelector } from 'app/hooks';
import { selectOfferPlanCode } from 'services/offer/offerSelectors';
import { useDashboardSummary } from 'contexts/DashboardSummaryContext';
import type { DashboardSummary } from 'services/dashboard/types';
import type { ServicesType } from 'utils/globalTypes';
import UnreadAlertsDot from 'modules/family-member-management/components/UnreadAlertsDot';
import unreadDotStyles from 'modules/family-member-management/components/UnreadAlertsDot/index.module.scss';
import useChildAlertsUnreadCounts from 'modules/family-member-management/hooks/useChildAlertsUnreadCounts';
import { MenuItem, NavLeafConfig, NavLinkItemProps } from './types';
import {
  NAV_SECTIONS,
  isChildInnerPage,
  PLAN_MEMBERS_PATH,
} from './menu-config';
import styles from './index.module.scss';

// The sidebar's alerts icon switches between the `alerts` (red-dot)
// and `alertsZero` (no-dot) variants based on whether the user has
// any UNREAD alerts. Sourced from the dashboard summary's top-level
// `alertCounts.unread` field — NOT from the per-category unresolved
// counts. Unresolved counts only drop when an alert is archived, so
// using them kept the red dot visible after the user had read every
// alert. Unread drops the moment an alert is opened, matching the
// expected UX.
function computeUnreadTotal(summary: DashboardSummary | null): number {
  return summary?.alertCounts?.unread ?? 0;
}

function getItem(
  key: React.Key,
  label: React.ReactNode,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: 'group',
  qaSelector?: string,
): MenuItem {
  return {
    key,
    label,
    icon,
    children,
    type,
    'data-qa-selector': qaSelector,
  } as MenuItem;
}

// Path used by the NavLink `.link.active` override below. Child-inner
// routes still light up the anchor for the My Family row (not the
// feature leaf whose prefix they happen to share); the `isChildInnerPage`
// helper is imported from `menu-config` so this file and the antd
// selectedKeys/openKeys resolver read from a single source of truth.
const FAMILY_MENU_HIGHLIGHT_PATH = PLAN_MEMBERS_PATH;

function NavLinkItem(props: NavLinkItemProps) {
  const {
    navigateTo = '/',
    highlightPath,
    label,
    qaLabel,
    icon,
    className = '',
    iconBadge,
  } = props;
  const navigate = useNavigate();
  const location = useLocation();
  // `qaLabel` decouples the selector from the visible label: it pins the
  // selector when a label is reworded, and supplies plain text for labels that
  // are JSX (a React element stringifies to "[object Object]").
  const qaSource = qaLabel ?? label;
  const qaSelector = `sidebar_${qaSource?.toString().toLowerCase().replaceAll(' ', '-')}`;
  const onChildInnerPage = isChildInnerPage(location.pathname);
  const targetPath = highlightPath ?? navigateTo;

  // Navlink Item click handler
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (navigateTo === '/dark-web-scan-report') {
      window.open(LINKS.cyexScan, '_blank', 'noopener,noreferrer');
    } else {
      navigate(navigateTo);
    }
  };

  return (
    <NavLink
      to={targetPath}
      data-qa-selector={qaSelector}
      className={({ isActive }) => {
        // On a child inner page force "My Family" active and every
        // other sidebar entry inactive; elsewhere fall back to the
        // NavLink prefix match.
        const effectiveActive = onChildInnerPage
          ? targetPath === FAMILY_MENU_HIGHLIGHT_PATH
          : isActive;
        return clsx(styles.link, {
          [styles.active]: effectiveActive,
          [styles.strokeLink]: !!className,
        });
      }}
      onClick={handleClick}
    >
      {icon ? (
        <span className={styles.menuIconWrap}>
          <LazySvg name={icon} className={styles.menuIcon} />
          {iconBadge && (
            <span className={styles.menuIconBadge}>{iconBadge}</span>
          )}
        </span>
      ) : null}
      {label}
    </NavLink>
  );
}

const MomoizedNavLinkItem = memo(NavLinkItem);

// Section children render as text-only rows (Figma): no icon, and the external
// ones get a trailing arrow glyph appended to the label.
function buildLeafItem(leaf: NavLeafConfig): MenuItem {
  const label = leaf.external ? (
    <>
      {leaf.label} <LazySvg name='arrow-up-right' />
    </>
  ) : (
    leaf.label
  );

  return getItem(
    leaf.key,
    <MomoizedNavLinkItem
      navigateTo={leaf.navigateTo}
      highlightPath={leaf.highlightPath}
      label={label}
      qaLabel={leaf.qaLabel ?? leaf.label}
      className={leaf.className}
    />,
  );
}

function useSidebarMenuItems() {
  const { summary } = useDashboardSummary();
  const unreadAlertsCount = computeUnreadTotal(summary);

  /* Getting user access to the services based on entitlments */
  const hasAlerts = useIsFeatureAvailable(SERVICES.alerts);
  const hasFamilyPlan = useIsFeatureAvailable(SERVICES.familyPlan);
  // My Family menu badge — red dot when ANY child has unread (new)
  // alerts. Per-child totals come from /api/client/alerts
  // `totalUnreadCount`, matching plan-members row dots — not the
  // dashboard summary's per-child unresolved counts (those stay high
  // until an alert is archived, same bug the Alerts icon had).
  const { hasAnyUnread: showFamilyUnreadDot } =
    useChildAlertsUnreadCounts(hasFamilyPlan);

  // One entry per service the accordion can gate on. Hooks cannot be called
  // while looping the config, so they are collected up front and looked up by
  // service key when the config is mapped.
  const available: Partial<Record<ServicesType, boolean>> = {
    [SERVICES.vpnInTouch]: useIsFeatureAvailable(SERVICES.vpnInTouch),
    [SERVICES.passwordDefense]: useIsFeatureAvailable(SERVICES.passwordDefense),
    [SERVICES.passwordManager]: useIsFeatureAvailable(SERVICES.passwordManager),
    [SERVICES.antivirus]: useIsFeatureAvailable(SERVICES.antivirus),
    [SERVICES.dboo]: useIsFeatureAvailable(SERVICES.dboo),
    [SERVICES.passwords]: useIsFeatureAvailable(SERVICES.passwords),
    [SERVICES.digitalVault]: useIsFeatureAvailable(SERVICES.digitalVault),
    [SERVICES.medicalDataMonitoring]: useIsFeatureAvailable(
      SERVICES.medicalDataMonitoring,
    ),
    [SERVICES.darkweb]: useIsFeatureAvailable(SERVICES.darkweb),
    [SERVICES.socialMedia]: useIsFeatureAvailable(SERVICES.socialMedia),
    [SERVICES.darkwebScanReport]: useIsFeatureAvailable(
      SERVICES.darkwebScanReport,
    ),
    [SERVICES.passwordScan]: useIsFeatureAvailable(SERVICES.passwordScan),
    [SERVICES.htmon]: useIsFeatureAvailable(SERVICES.htmon),
    [SERVICES.publicRecords]: useIsFeatureAvailable(SERVICES.publicRecords),
    [SERVICES.publicRecordTrace]: useIsFeatureAvailable(
      SERVICES.publicRecordTrace,
    ),
    [SERVICES.creditScore]: useIsFeatureAvailable(SERVICES.creditScore),
    [SERVICES.creditScoreSimulator]: useIsFeatureAvailable(
      SERVICES.creditScoreSimulator,
    ),
    [SERVICES.creditLock]: useIsFeatureAvailable(SERVICES.creditLock),
    [SERVICES.creditReport]: useIsFeatureAvailable(SERVICES.creditReport),
    [SERVICES.bankAccountMonitoring]: useIsFeatureAvailable(
      SERVICES.bankAccountMonitoring,
    ),
    [SERVICES.walletProtection]: useIsFeatureAvailable(
      SERVICES.walletProtection,
    ),
    [SERVICES.securityFreeze]: useIsFeatureAvailable(SERVICES.securityFreeze),
    [SERVICES.financialCalculator]: useIsFeatureAvailable(
      SERVICES.financialCalculator,
    ),
    [SERVICES.spendingTracker]: useIsFeatureAvailable(SERVICES.spendingTracker),
    [SERVICES.txmon]: useIsFeatureAvailable(SERVICES.txmon),
    [SERVICES.identityMonitoring]: useIsFeatureAvailable(
      SERVICES.identityMonitoring,
    ),
    [SERVICES.insurance]: useIsFeatureAvailable(SERVICES.insurance),
  };

  const planCode = useAppSelector(selectOfferPlanCode);
  // mshield brand and htitleind/htitlefam plans exclude transaction monitoring nav item
  const TXMON_EXCLUDED_PLANS = ['htitleind', 'htitlefam'];
  available[SERVICES.txmon] =
    available[SERVICES.txmon] && !TXMON_EXCLUDED_PLANS.includes(planCode ?? '');
  const isLeafVisible = (leaf: NavLeafConfig): boolean =>
    !leaf.service || !!available[leaf.service];

  const menuItems: ItemType[] = [
    getItem(
      'dashboard',
      <MomoizedNavLinkItem
        navigateTo='/dashboard'
        label='Dashboard'
        icon='dashboard'
      />,
    ),
  ];

  if (hasAlerts) {
    menuItems.push(
      getItem(
        'alerts',
        <MomoizedNavLinkItem
          navigateTo='/alerts'
          label='Alerts'
          icon={unreadAlertsCount ? 'alerts' : 'alertsZero'}
        />,
      ),
    );
  }

  /* Accordion sections — a section is dropped once none of its children are
     entitled; single-child sections still render as accordions (Figma). */
  NAV_SECTIONS.forEach((section) => {
    const children = section.items.filter(isLeafVisible).map(buildLeafItem);

    if (children.length) {
      menuItems.push(
        getItem(
          section.key,
          // The icon lives inside the label rather than antd's `icon` slot:
          // `inlineIndent` writes an inline `padding-left` onto the submenu
          // title, which no stylesheet rule can beat, so the row's left inset
          // has to come from an element we own.
          <Flex align='center' className={styles.sectionTitle}>
            <LazySvg name={section.icon} className={styles.menuIcon} />
            {section.label}
          </Flex>,
          null,
          children,
          undefined,
          `sidebar_section_${section.key}`,
        ),
      );
    }
  });

  // My Family is a flat row rather than a section, and sits last in the menu —
  // after every accordion. Pushed outside the section loop (not gated on any
  // section rendering) so it still appears when no section is entitled.
  if (hasFamilyPlan) {
    menuItems.push(
      getItem(
        'plan-members',
        <MomoizedNavLinkItem
          navigateTo='/my-account/plan-members'
          highlightPath='/my-account/plan-members'
          label='My Family'
          icon='user-group'
          iconBadge={
            showFamilyUnreadDot ? (
              <UnreadAlertsDot className={unreadDotStyles.menuIconCorner} />
            ) : undefined
          }
        />,
      ),
    );
  }

  return menuItems;
}

export default useSidebarMenuItems;
